import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import { X, MessageSquare, Check, XCircle, FileText, Briefcase, MapPin, DollarSign, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface CandidateDetailModalProps {
  open: boolean;
  candidate: any; // mapped candidate object
  onClose: () => void;
  onAccept?: (id: string) => void;
  onReject?: (id: string) => void;
}

export function CandidateDetailModal({ open, candidate, onClose, onAccept, onReject }: CandidateDetailModalProps) {
  if (!candidate) return null;

  const raw = candidate.rawProfile || {};
  const status = candidate.status;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      sx={{

        borderRadius: '28px',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
        border: '1px solid #f1f3f5'

      }}
    >
      {/* Gradient Banner Header */}
      <div className="bg-gradient-to-r from-[#f97316] to-[#ff7e2e] p-6 text-white relative flex items-start justify-between shrink-0">
        <div className="flex gap-4 items-center">
          {candidate.avatar ? (
            <Avatar src={candidate.avatar} alt={candidate.fullName} sx={{ width: 70, height: 70, border: '3px solid rgba(255,255,255,0.45)', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }} />
          ) : (
            <div className="w-[70px] h-[70px] rounded-full border-[3px] border-white/40 bg-white/20 shadow-sm flex items-center justify-center text-3xl font-bold uppercase shrink-0">
              {candidate.fullName?.charAt(0) || 'U'}
            </div>
          )}
          <div className="space-y-1">
            <h3 className="text-xl font-bold leading-tight">{candidate.fullName}</h3>
            <span className="text-[13px] text-white/90 font-black uppercase tracking-wider block">{candidate.position}</span>
          </div>
        </div>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.15)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' } }}
          size="small"
        >
          <X size={16} />
        </IconButton>
      </div>

      {/* Scrollable details */}
      <DialogContent sx={{ p: 0 }}>
        <div className="p-6 flex flex-col gap-6">

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 p-3 rounded-2xl border border-gray-100 flex flex-col items-center text-center">
              <Briefcase size={18} className="text-gray-400 mb-1" />
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Kinh nghiệm</span>
              <span className="text-xs font-semibold text-gray-800 line-clamp-1">{candidate.experience}</span>
            </div>
            <div className="bg-gray-50 p-3 rounded-2xl border border-gray-100 flex flex-col items-center text-center">
              <DollarSign size={18} className="text-green-500 mb-1" />
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Mức lương</span>
              <span className="text-xs font-semibold text-gray-800 line-clamp-1">{candidate.salary}</span>
            </div>
            <div className="bg-gray-50 p-3 rounded-2xl border border-gray-100 flex flex-col items-center text-center">
              <MapPin size={18} className="text-blue-400 mb-1" />
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Khu vực</span>
              <span className="text-xs font-semibold text-gray-800 line-clamp-1">{candidate.location}</span>
            </div>
            <div className="bg-gray-50 p-3 rounded-2xl border border-gray-100 flex flex-col items-center text-center">
              <BookOpen size={18} className="text-purple-400 mb-1" />
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Học vấn</span>
              <span className="text-xs font-semibold text-gray-800 line-clamp-1">{candidate.education}</span>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-3">
            <h4 className="font-bold text-gray-800 uppercase tracking-wider text-[11px] flex items-center gap-1.5 border-b pb-2">
              Thông tin liên hệ
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4 text-sm">
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mb-0.5">Email</span>
                <span className="text-gray-800 font-medium break-all">{candidate.email}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mb-0.5">Số điện thoại</span>
                <span className="text-gray-800 font-medium">{candidate.phoneNumber || 'Chưa cập nhật'}</span>
              </div>
            </div>
          </div>

          {/* Skills */}
          {raw.skills && raw.skills.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-bold text-gray-800 uppercase tracking-wider text-[11px] flex items-center gap-1.5 border-b pb-2">
                Kỹ năng
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {raw.skills.map((skill: string, idx: number) => (
                  <span key={idx} className="bg-orange-50 text-orange-600 border border-orange-100 text-xs px-2.5 py-1 rounded-lg font-medium">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Cover Letter / Introduction Message */}
          <div className="space-y-3">
            <h4 className="font-bold text-gray-800 uppercase tracking-wider text-[11px] flex items-center gap-1.5 border-b pb-2">
              Thư giới thiệu (Cover Letter)
            </h4>
            <div className="bg-[#f8fafc] rounded-2xl p-4 border border-gray-100 text-gray-700 leading-relaxed text-sm font-medium whitespace-pre-wrap">
              {raw.coverLetter || 'Ứng viên chưa đính kèm thư giới thiệu.'}
            </div>
          </div>

          {/* CV Attachment */}
          {raw.resumeUrl && (
            <div className="space-y-3">
              <h4 className="font-bold text-gray-800 uppercase tracking-wider text-[11px] flex items-center gap-1.5 border-b pb-2">
                Hồ sơ đính kèm
              </h4>
              <a href={raw.resumeUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 border border-blue-100 px-4 py-2.5 rounded-xl hover:bg-blue-100 transition-colors">
                <FileText size={18} />
                Xem CV / Resume
              </a>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Modal Actions */}
      <DialogActions sx={{ p: 2.5, backgroundColor: '#f9fafb', borderTop: '1px solid #f1f3f5', gap: 1 }}>
        <Link to="/tin-nhan" className="flex-1" onClick={onClose}>
          <Button variant="outline" className="w-full h-11 rounded-2xl border-gray-200 hover:bg-white text-gray-700 font-bold flex items-center justify-center gap-1.5 shadow-sm text-xs">
            <MessageSquare size={15} /> Nhắn tin
          </Button>
        </Link>

        {status === 'screening' || status === 'interview' || status === 'evaluation' ? (
          <>
            <Button
              onClick={() => onReject?.(candidate.id)}
              className="h-11 rounded-2xl border-red-200 text-red-600 bg-white hover:bg-red-50 hover:text-red-700 font-bold border flex items-center justify-center gap-1.5 px-4 shadow-sm text-xs"
            >
              <XCircle size={15} /> Từ chối
            </Button>

            <Button
              onClick={() => onAccept?.(candidate.id)}
              className="h-11 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-bold flex items-center justify-center gap-1.5 px-5 shadow shadow-green-100 text-xs"
            >
              <Check size={15} /> Nhận hồ sơ
            </Button>
          </>
        ) : (
          <div className="flex-1 text-center py-2.5 text-xs text-gray-400 font-semibold italic">
            Hồ sơ này đã được xử lý xong
          </div>
        )}
      </DialogActions>
    </Dialog>
  );
}
